import WindsorApi, { WorfklowExecutionOptions } from '../../api/WindsorApi';
import { ExecutionStatus } from './ExecutionConstants';
import ExecutionTableRowBuilder from './ExecutionTableRowBuilder';

export enum ExecutionEventType {
    ExecutionCreated,
    ExecutionUpdated
}

class ExecutionStore {
    private windsorApi: WindsorApi;
    private executionTableRowBuilder: ExecutionTableRowBuilder;
    private listeners: any[] = [];

    private executionsGroupMap: any = {};
    private executionMap: any = {};
    private pendingPromises: any = {};

    constructor(windsorApi: WindsorApi) {
        this.windsorApi = windsorApi;
        this.executionTableRowBuilder = new ExecutionTableRowBuilder();
    }

    addExecutionListener(listener: (eventType: ExecutionEventType, eventData: any) => void) {
        this.listeners.push(listener);
        return () => {
            // Remove listener
            this.listeners.splice(this.listeners.indexOf(listener), 1);
        };
    }

    getWorkflowExecutions(familyId: string, workflowId: string) {
        const key = `${familyId}:${workflowId}`;
        return this._getExecutions(key, () => this.windsorApi.getWorkflowExecutions(familyId, workflowId));
    }

    getFamilyExecutions(familyId: string) {
        return this._getExecutions(familyId, () => this.windsorApi.getExecutions(familyId));
    }

    executeWorkflow(familyId: string, workflowId: string, options: WorfklowExecutionOptions) {
        return this.windsorApi.executeWorkflow(familyId, workflowId, options).then((res) => {
            this.windsorApi.getExecution(familyId, res.GUID).then(execution => {
                const executionModel = this.executionTableRowBuilder.build(execution);

                const executionsByWorkflow = this.executionsGroupMap[`${familyId}:${workflowId}`];
                if (executionsByWorkflow && !executionsByWorkflow.some(({ id }) => id === executionModel.id)) {
                    executionsByWorkflow.push(executionModel);
                }

                if (this.executionsGroupMap[familyId]) {
                    this.executionsGroupMap[familyId].push(executionModel);
                }

                // Remove any pending requests so that they get the latest data
                delete this.pendingPromises[`${familyId}:${workflowId}`];
                delete this.pendingPromises[familyId];

                this.executionMap[executionModel.id] = executionModel;

                this._notifyListeners(ExecutionEventType.ExecutionCreated, executionModel);

                // Start watcher if execution is still in progress
                if (executionModel.executionStatus === ExecutionStatus.InProgress) {
                    this._watchExecution(executionModel);
                }
            });

            return res;
        });
    }

    runExecution(familyId: string, executionId: string) {
        return this.windsorApi.runExecution(familyId, executionId)
            .then(execution => this._updateCachedExecution(execution));
    }

    cancelExecution(familyId: string, executionId: string) {
        return this.windsorApi.cancelExecution(familyId, executionId)
            .then(execution => this._updateCachedExecution(execution));
    }

    forceCancelExecution(familyId: string, executionId: string) {
        return this.windsorApi.forceCancelExecution(familyId, executionId)
            .then(execution => this._updateCachedExecution(execution));
    }

    _watchExecution(execution: any) {
        setTimeout(() => this.windsorApi.getExecution(execution.familyId, execution.id).then(execution => {
            this._updateCachedExecution(execution);
        }), 30000);
    }

    _updateCachedExecution(execution) {
        const id = execution.GUID;

        if (this.executionMap[id]) {
            const executionModel = this.executionTableRowBuilder.build(execution);
            // Update execution
            Object.assign(this.executionMap[id], executionModel);

            this._notifyListeners(ExecutionEventType.ExecutionUpdated, executionModel);

            if (executionModel.executionStatus === ExecutionStatus.InProgress) {
                this._watchExecution(executionModel);
            }
        }

        return execution;
    }

    _getExecutions(key: string, callback: () => Promise<any>) {
        if (this.pendingPromises[key]) {
            return this.pendingPromises[key];
        } else if (this.executionsGroupMap[key]) {
            // Return copy of array
            return Promise.resolve([...this.executionsGroupMap[key]]);
        } else {
            this.pendingPromises[key] = callback().then(response => {
                this.executionsGroupMap[key] = response.map(item => {
                    const executionModel = this.executionTableRowBuilder.build(item);
                    this.executionMap[executionModel.id] = executionModel;
                    if (executionModel.executionStatus === ExecutionStatus.InProgress) {
                        this._watchExecution(executionModel);
                    }
                    return executionModel;
                });
                delete this.pendingPromises[key];
                // Return copy of array
                return [...this.executionsGroupMap[key]];
            });
            return this.pendingPromises[key];
        }
    }

    _notifyListeners(eventType: ExecutionEventType, eventData: any) {
        this.listeners.forEach(listener => listener(eventType, eventData));
    }
}

export default ExecutionStore;